import type { PlanId } from "@/lib/branchTypes"
import type {
  GraphDataFile,
  GraphDatum,
  LossOfControlBoundsForPlan,
} from "@/app/playbook/extrapolated-graph/types"

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000
const APPROX_DAYS_PER_MONTH = 30.4375

const dateStringToTimestamp = (dateString: string) =>
  new Date(`${dateString}T00:00:00Z`).getTime()

const formatIsoDate = (timestamp: number) =>
  new Date(timestamp).toISOString().slice(0, 10)

const shiftDateByMonths = (dateString: string, months: number) => {
  if (months === 0) return dateString
  const baseTimestamp = dateStringToTimestamp(dateString)
  const shiftedTimestamp = baseTimestamp + (months * APPROX_DAYS_PER_MONTH * MILLISECONDS_PER_DAY)
  return formatIsoDate(shiftedTimestamp)
}

const interpolateGraphValue = (data: GraphDatum[], targetTimestamp: number) => {
  if (data.length === 0) return 0
  const firstTimestamp = dateStringToTimestamp(data[0].date)
  if (targetTimestamp <= firstTimestamp) return data[0].value
  const lastTimestamp = dateStringToTimestamp(data[data.length - 1].date)
  if (targetTimestamp >= lastTimestamp) return data[data.length - 1].value
  for (let segmentStartIndex = 0; segmentStartIndex < data.length - 1; segmentStartIndex++) {
    const segmentStart = data[segmentStartIndex]
    const segmentEnd = data[segmentStartIndex + 1]
    const segmentStartTimestamp = dateStringToTimestamp(segmentStart.date)
    const segmentEndTimestamp = dateStringToTimestamp(segmentEnd.date)
    if (targetTimestamp > segmentEndTimestamp) continue
    const segmentSpan = segmentEndTimestamp - segmentStartTimestamp
    const progressWithinSegment = segmentSpan === 0
      ? 0
      : (targetTimestamp - segmentStartTimestamp) / segmentSpan
    return segmentStart.value + ((segmentEnd.value - segmentStart.value) * progressWithinSegment)
  }
  return data[data.length - 1].value
}

const applySafetyTaxShiftToSeries = (
  data: GraphDatum[],
  currentDate: Date,
  shiftMonths: number,
): GraphDatum[] => {
  if (shiftMonths === 0 || data.length === 0) return data
  const currentTimestamp = currentDate.getTime()
  const pastData = data.filter(datum => dateStringToTimestamp(datum.date) <= currentTimestamp)
  const futureData = data.filter(datum => dateStringToTimestamp(datum.date) > currentTimestamp)
  if (futureData.length === 0) return data
  const shiftedFutureData = futureData.map(datum => ({
    date: shiftDateByMonths(datum.date, shiftMonths),
    value: datum.value,
  }))
  if (pastData.length === 0) return shiftedFutureData
  const currentValue = interpolateGraphValue(data, currentTimestamp)
  const shiftedCurrentDate = shiftDateByMonths(formatIsoDate(currentTimestamp), shiftMonths)
  return [
    ...pastData,
    { date: shiftedCurrentDate, value: currentValue },
    ...shiftedFutureData,
  ]
}

const applyAlignmentDoublingsToSeries = (
  data: GraphDatum[],
  currentDate: Date,
  doublings: number,
): GraphDatum[] => {
  if (doublings === 0 || data.length === 0) return data
  const deltaMultiplier = Math.pow(1.5, doublings)
  const currentTimestamp = currentDate.getTime()
  const adjustedData: GraphDatum[] = []
  for (let pointIndex = 0; pointIndex < data.length; pointIndex++) {
    const originalDatum = data[pointIndex]
    if (pointIndex === 0) {
      adjustedData.push(originalDatum)
      continue
    }
    const originalPreviousDatum = data[pointIndex - 1]
    const adjustedPreviousDatum = adjustedData[pointIndex - 1]
    const originalDatumTimestamp = dateStringToTimestamp(originalDatum.date)
    if (originalDatumTimestamp <= currentTimestamp) {
      adjustedData.push(originalDatum)
      continue
    }
    const originalDelta = originalDatum.value - originalPreviousDatum.value
    const adjustedValue = adjustedPreviousDatum.value + (originalDelta * deltaMultiplier)
    adjustedData.push({ date: originalDatum.date, value: adjustedValue })
  }
  return adjustedData
}

const PLAN_DATA_KEYS = ['planS', 'planA', 'planB', 'planBUs', 'planBCh', 'planC', 'planD'] as const

export const buildAdjustedGraphDataFile = ({
  graphDataFile,
  currentDate,
  safetyTaxShiftMonths,
  alignmentDoublings,
}: {
  graphDataFile: GraphDataFile
  currentDate: Date
  safetyTaxShiftMonths: number
  alignmentDoublings: number
}): GraphDataFile => {
  if (safetyTaxShiftMonths === 0 && alignmentDoublings === 0) return graphDataFile
  const shiftedShared = applySafetyTaxShiftToSeries(
    graphDataFile.shared,
    currentDate,
    safetyTaxShiftMonths,
  )
  const shiftedPlanSeries = PLAN_DATA_KEYS.reduce((accumulator, planKey) => {
    const originalSeries = graphDataFile[planKey]
    if (!originalSeries) return accumulator
    accumulator[planKey] = applySafetyTaxShiftToSeries(originalSeries, currentDate, safetyTaxShiftMonths)
    return accumulator
  }, {} as Partial<Record<typeof PLAN_DATA_KEYS[number], GraphDatum[]>>)
  const planIds: PlanId[] = ['S', 'A', 'B', 'C', 'D']
  const adjustedLossOfControlByPlan = planIds.reduce(
    (accumulator, planId) => {
      const planBounds = graphDataFile.lossOfControlByPlan[planId]
      accumulator[planId] = {
        min: applyAlignmentDoublingsToSeries(planBounds.min, currentDate, alignmentDoublings),
        max: applyAlignmentDoublingsToSeries(planBounds.max, currentDate, alignmentDoublings),
      }
      return accumulator
    },
    {} as Record<PlanId, LossOfControlBoundsForPlan>,
  )
  return {
    ...graphDataFile,
    shared: shiftedShared,
    ...shiftedPlanSeries,
    lossOfControlByPlan: adjustedLossOfControlByPlan,
  }
}
